import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import { ApiError } from '@/api/client';
import { DataGrid } from '@/components/DataGrid';
import { useClientDataGrid } from '@/components/DataGrid/useClientDataGrid';
import { useAlert, useConfirm } from '@/components/ConfirmDialog';
import { SpriteIcon } from '@/components/SpriteIcon';
import { useToast } from '@/components/Toast';
import {
  campaignLabelKey,
  orphansUrl,
  useActiveImport,
  useCancelImport,
  useImportCampaigns,
  useImportJobs,
  useSubmitImport,
  type ImportEvent,
  type ImportFileRole,
  type ImportJob,
  type ImportJobStatus,
  type ImportMode,
} from './useImports';
import { useImportStream } from './useImportStream';

/**
 * Replaces the `stla-takata-import` console tool.
 *
 * An administrator uploads the supplier's CSVs, the import runs in the background on whichever
 * instance took the upload, and its log streams back here over SignalR. Everything the CLI put
 * on a terminal is on this page: the parse counts, the per-wave progress, the final summary,
 * and the orphan rows — which used to be written to a file next to the input folder and are
 * now a download.
 *
 * Only one import may run across the whole fleet, so the form is disabled — for every
 * administrator, not only the one who started it — while a run is in progress. That banner is
 * driven by `GET /active`, which is why it appears on a second browser as well.
 */

const MODES: ImportMode[] = ['DryRun', 'New', 'Update'];

/** Every role the form can show, in the order the supplier's folder lists them. */
const ROLE_ORDER: ImportFileRole[] = [
  'garages',
  'calls',
  'appointments',
  'regionalStats',
  'subRegionalStats',
  'thresholds',
];

const STATUS_TONE: Record<ImportJobStatus, string> = {
  Queued: 'bg-secondary',
  Running: 'bg-primary',
  Succeeded: 'bg-success',
  Failed: 'bg-danger',
  Cancelled: 'bg-warning text-dark',
};

const LEVEL_CLASS = {
  info: 'text-body',
  warn: 'text-warning-emphasis',
  error: 'text-danger',
} as const;

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

function formatTime(value: string): string {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleTimeString();
}

export function DataImportPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const alert = useAlert();
  const confirm = useConfirm();

  const campaigns = useImportCampaigns();
  const jobs = useImportJobs();
  const active = useActiveImport();
  const submit = useSubmitImport();
  const cancel = useCancelImport();

  const [campaign, setCampaign] = useState('');
  const [mode, setMode] = useState<ImportMode>('DryRun');
  const [files, setFiles] = useState<Partial<Record<ImportFileRole, File>>>({});

  // Which run the log panel is showing. Follows the active job while one exists, and otherwise
  // whatever the operator picked from the history.
  const [watchedJobId, setWatchedJobId] = useState<string | null>(null);

  // Lets the file inputs be cleared after a successful submission; they are uncontrolled, so
  // resetting `files` alone would leave the chosen names on screen.
  const formRef = useRef<HTMLFormElement>(null);

  const activeJob = active.data ?? null;

  // The first campaign is the default, and the form resets its files when it changes: the
  // roles differ per campaign, so keeping them would submit a file the campaign has no slot for.
  useEffect(() => {
    if (!campaign && campaigns.data?.length) setCampaign(campaigns.data[0].campaign);
  }, [campaign, campaigns.data]);

  useEffect(() => {
    if (activeJob) setWatchedJobId(activeJob.id);
  }, [activeJob]);

  const selected = useMemo(
    () => campaigns.data?.find((entry) => entry.campaign === campaign) ?? null,
    [campaigns.data, campaign],
  );

  const roles = useMemo(() => {
    if (!selected) return [];
    const known = new Set<ImportFileRole>([...selected.requiredFileRoles, ...selected.optionalFileRoles]);
    return ROLE_ORDER.filter((role) => known.has(role)).map((role) => ({
      role,
      required: selected.requiredFileRoles.includes(role),
    }));
  }, [selected]);

  const missingRequired = useMemo(
    () => (selected?.requiredFileRoles ?? []).filter((role) => !files[role]),
    [selected, files],
  );

  const stream = useImportStream(watchedJobId);
  const watchedJob = useMemo(
    () => (activeJob?.id === watchedJobId ? activeJob : jobs.data?.find((job) => job.id === watchedJobId) ?? null),
    [activeJob, jobs.data, watchedJobId],
  );

  // A finished run has to refresh the history and clear the banner; the active query is
  // polled, but the list is not.
  const previousActiveId = useRef<string | null>(null);
  useEffect(() => {
    if (previousActiveId.current && !activeJob) {
      jobs.refetch();
      const finished = jobs.data?.find((job) => job.id === previousActiveId.current);
      if (finished?.status === 'Succeeded') {
        toast({ variant: 'success', message: t('DataImport.Finished', 'The import finished successfully.') });
      }
    }
    previousActiveId.current = activeJob?.id ?? null;
    // `jobs` is a query object with a new identity each render; depending on it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeJob, t, toast]);

  const handleFile = useCallback((role: ImportFileRole, file: File | null) => {
    setFiles((current) => {
      const next = { ...current };
      if (file) next[role] = file;
      else delete next[role];
      return next;
    });
  }, []);

  const handleCampaignChange = useCallback((value: string) => {
    setCampaign(value);
    setFiles({});
    formRef.current?.reset();
  }, []);

  const handleSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      if (!selected || submit.isPending) return;

      if (mode !== 'DryRun') {
        const confirmed = await confirm({
          title: t('DataImport.ConfirmTitle', 'Write to the database?'),
          message:
            mode === 'New'
              ? t('DataImport.ConfirmNew', 'This will insert the waves found in these files. Continue?')
              : t(
                  'DataImport.ConfirmUpdate',
                  'This will delete the existing rows of each wave found in these files and re-insert them. Continue?',
                ),
        });
        if (!confirmed) return;
      }

      submit.mutate(
        { campaign: selected.campaign, mode, files },
        {
          onSuccess: (job) => {
            setWatchedJobId(job.id);
            setFiles({});
            formRef.current?.reset();
            toast({ variant: 'info', message: t('DataImport.Queued', 'The import has been queued.') });
          },
          onError: (error) => {
            const problem = error instanceof ApiError ? error : null;
            const detail =
              problem?.status === 409
                ? t('DataImport.AlreadyRunning', 'Another import is already queued or running.')
                : (problem?.message ?? t('DataImport.SubmitFailed', 'The import could not be submitted.'));
            alert({ variant: 'danger', title: t('DataImport.SubmitFailed', 'The import could not be submitted.'), message: detail });
          },
        },
      );
    },
    [alert, confirm, files, mode, selected, submit, t, toast],
  );

  const handleCancel = useCallback(async () => {
    if (!activeJob) return;
    const confirmed = await confirm({
      title: t('DataImport.CancelTitle', 'Cancel the running import?'),
      message: t(
        'DataImport.CancelMessage',
        'The wave being written is rolled back. Waves already committed stay in the database.',
      ),
    });
    if (!confirmed) return;

    cancel.mutate(activeJob.id, {
      onSuccess: () =>
        toast({ variant: 'warning', message: t('DataImport.CancelRequested', 'Cancellation requested.') }),
      onError: () =>
        alert({
          title: t('DataImport.CancelFailed', 'The import could not be cancelled.'),
          message: t('DataImport.CancelFailedDetail', 'It may already have finished.'),
        }),
    });
  }, [activeJob, alert, cancel, confirm, t, toast]);

  const canSubmit = Boolean(selected) && missingRequired.length === 0 && !activeJob && !submit.isPending;

  return (
    <div className="container-fluid py-3">
      <div className="mb-3">
        <h1 className="h3 mb-1">{t('DataImport.Title', 'Data import')}</h1>
        <p className="text-muted mb-0">
          {t(
            'DataImport.Subtitle',
            'Upload the mystery-calls CSVs. The import runs in the background and its log appears below.',
          )}
        </p>
      </div>

      {activeJob && (
        <div className="alert alert-info d-flex align-items-center justify-content-between" role="status">
          <span>
            <SpriteIcon name="activity" size={16} className="me-2" />
            {t('DataImport.InProgress', 'An import started by {{user}} is {{status}}.', {
              user: activeJob.requestedBy,
              status: activeJob.status.toLowerCase(),
            })}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            onClick={handleCancel}
            disabled={activeJob.cancelRequested || cancel.isPending}
          >
            {activeJob.cancelRequested
              ? t('DataImport.Cancelling', 'Cancelling…')
              : t('DataImport.Cancel', 'Cancel')}
          </button>
        </div>
      )}

      <div className="card shadow-sm mb-4">
        <div className="card-header bg-transparent">
          <h5 className="mb-0">{t('DataImport.NewImport', 'New import')}</h5>
        </div>
        <div className="card-body">
          <form ref={formRef} onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label" htmlFor="di-campaign">
                  {t('DataImport.Campaign', 'Campaign')}
                </label>
                <select
                  id="di-campaign"
                  className="form-select"
                  value={campaign}
                  onChange={(event) => handleCampaignChange(event.target.value)}
                  disabled={campaigns.isPending}
                >
                  {(campaigns.data ?? []).map((entry) => (
                    <option key={entry.campaign} value={entry.campaign}>
                      {t(campaignLabelKey(entry.campaign), entry.campaign)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-8 mb-3">
                <span className="form-label d-block">{t('DataImport.Mode', 'Mode')}</span>
                <div className="d-flex flex-wrap gap-3">
                  {MODES.map((value) => (
                    <div className="form-check" key={value}>
                      <input
                        className="form-check-input"
                        type="radio"
                        name="mode"
                        id={`di-mode-${value}`}
                        checked={mode === value}
                        onChange={() => setMode(value)}
                      />
                      <label className="form-check-label" htmlFor={`di-mode-${value}`}>
                        {value === 'DryRun' && t('DataImport.ModeDryRun', 'Dry run (no writes)')}
                        {value === 'New' && t('DataImport.ModeNew', 'New wave')}
                        {value === 'Update' && t('DataImport.ModeUpdate', 'Update existing wave')}
                      </label>
                    </div>
                  ))}
                </div>
                <div className="form-text">
                  {mode === 'DryRun' &&
                    t('DataImport.ModeDryRunHelp', 'Parses and checks the files. Nothing is written.')}
                  {mode === 'New' &&
                    t('DataImport.ModeNewHelp', 'Fails if any wave in the files is already in the database.')}
                  {mode === 'Update' &&
                    t(
                      'DataImport.ModeUpdateHelp',
                      'Deletes the existing rows of each wave and re-inserts them. Fails if a wave is missing.',
                    )}
                </div>
              </div>
            </div>

            <div className="row">
              {roles.map(({ role, required }) => (
                <div className="col-md-6 col-xl-4 mb-3" key={role}>
                  <label className="form-label" htmlFor={`di-file-${role}`}>
                    {t(`DataImport.Role.${role}`, role)}{' '}
                    {required ? (
                      <span className="badge bg-secondary">{t('DataImport.Required', 'required')}</span>
                    ) : (
                      <span className="badge bg-light text-muted border">
                        {t('DataImport.Optional', 'optional')}
                      </span>
                    )}
                  </label>
                  <input
                    id={`di-file-${role}`}
                    type="file"
                    accept=".csv,text/csv"
                    className="form-control"
                    onChange={(event) => handleFile(role, event.target.files?.[0] ?? null)}
                  />
                </div>
              ))}
            </div>

            <div className="d-flex align-items-center gap-3">
              <button type="submit" className="btn btn-primary" disabled={!canSubmit}>
                {submit.isPending
                  ? t('DataImport.Submitting', 'Uploading…')
                  : t('DataImport.Start', 'Start import')}
              </button>
              {missingRequired.length > 0 && (
                <span className="text-muted small">
                  {t('DataImport.MissingFiles', 'Still needed: {{roles}}', {
                    roles: missingRequired.join(', '),
                  })}
                </span>
              )}
            </div>
          </form>
        </div>
      </div>

      {watchedJobId && <RunPanel jobId={watchedJobId} job={watchedJob} stream={stream} />}

      <HistoryCard
        jobs={jobs.data ?? []}
        isLoading={jobs.isPending}
        selectedId={watchedJobId}
        onSelect={setWatchedJobId}
      />
    </div>
  );
}

/** Progress bar, scrolling log, summary and orphan download for one run. */
function RunPanel({
  jobId,
  job,
  stream,
}: {
  jobId: string;
  job: ImportJob | null;
  stream: ReturnType<typeof useImportStream>;
}) {
  const { t } = useTranslation();
  const logRef = useRef<HTMLDivElement>(null);

  // Follow the tail only while the operator has not scrolled up to read something.
  useEffect(() => {
    const element = logRef.current;
    if (!element) return;
    const atBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 80;
    if (atBottom) element.scrollTop = element.scrollHeight;
  }, [stream.events]);

  const percent = stream.step ? Math.round((stream.step.current / Math.max(stream.step.total, 1)) * 100) : 0;
  const running = job?.status === 'Running' || job?.status === 'Queued';

  return (
    <div className="card shadow-sm mb-4">
      <div className="card-header bg-transparent d-flex align-items-center justify-content-between">
        <h5 className="mb-0">
          {t('DataImport.Run', 'Run')}{' '}
          {job && <span className={`badge ${STATUS_TONE[job.status]} ms-2`}>{job.status}</span>}
        </h5>
        <span className="text-muted small">
          {stream.status === 'connected' && t('DataImport.Live', 'Live')}
          {stream.status === 'connecting' && t('DataImport.Connecting', 'Connecting…')}
          {stream.status === 'reconnecting' && t('DataImport.Reconnecting', 'Reconnecting…')}
          {stream.status === 'failed' && t('DataImport.NotLive', 'Not live — showing the stored log')}
        </span>
      </div>
      <div className="card-body">
        {running && (
          <div className="mb-3">
            <div className="d-flex justify-content-between small text-muted mb-1">
              <span>{stream.step?.name ?? t('DataImport.Starting', 'Starting…')}</span>
              <span>
                {stream.step ? `${stream.step.current} / ${stream.step.total}` : ''}
              </span>
            </div>
            <div className="progress" role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>
              <div
                className="progress-bar progress-bar-striped progress-bar-animated"
                style={{ width: `${percent}%` }}
              />
            </div>
          </div>
        )}

        {job?.error && (
          <div className="alert alert-danger" role="alert">
            {job.error}
          </div>
        )}

        <div
          ref={logRef}
          className="border rounded p-2 mb-3 font-monospace small"
          style={{ maxHeight: '22rem', overflowY: 'auto' }}
        >
          {stream.events.length === 0 ? (
            <span className="text-muted">{t('DataImport.NoEvents', 'No log lines yet.')}</span>
          ) : (
            stream.events.map((event) => <LogLine key={event.seq} event={event} />)
          )}
        </div>

        <div className="d-flex flex-wrap gap-3 align-items-start">
          {stream.summary && <SummaryCard summary={stream.summary} />}
          {stream.orphanSets.map((set) => (
            <a key={set} className="btn btn-outline-secondary btn-sm" href={orphansUrl(jobId, set)} download>
              <SpriteIcon name="download" size={14} className="me-1" />
              {set === 'appointments'
                ? t('DataImport.DownloadOrphanAppointments', 'Download orphan appointments')
                : t('DataImport.DownloadOrphanCalls', 'Download orphan calls')}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

function LogLine({ event }: { event: ImportEvent }) {
  return (
    <div className={LEVEL_CLASS[event.level] ?? 'text-body'}>
      <span className="text-muted me-2">{formatTime(event.ts)}</span>
      {event.message}
    </div>
  );
}

/** The pipeline's final counts. Rendered generically — each campaign reports its own keys. */
function SummaryCard({ summary }: { summary: Record<string, unknown> }) {
  const { t } = useTranslation();

  const entries = Object.entries(summary).filter(([, value]) => value !== null && value !== undefined);

  return (
    <div className="border rounded p-3">
      <h6 className="mb-2">{t('DataImport.Summary', 'Summary')}</h6>
      <dl className="row mb-0 small">
        {entries.map(([key, value]) => (
          <div className="col-6 col-md-4 mb-1" key={key}>
            <dt className="text-muted fw-normal">{key}</dt>
            <dd className="mb-0">{Array.isArray(value) ? value.join(', ') : String(value)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/** Past runs. Clicking one replays its log in the panel above. */
function HistoryCard({
  jobs,
  isLoading,
  selectedId,
  onSelect,
}: {
  jobs: ImportJob[];
  isLoading: boolean;
  selectedId: string | null;
  onSelect: (jobId: string) => void;
}) {
  const { t } = useTranslation();

  const columns = useMemo<ColumnDef<ImportJob, unknown>[]>(
    () => [
      {
        id: 'createdAt',
        accessorKey: 'createdAt',
        header: t('DataImport.Started', 'Started'),
        cell: ({ row }) => formatDateTime(row.original.createdAt),
      },
      {
        id: 'campaign',
        accessorKey: 'campaign',
        header: t('DataImport.Campaign', 'Campaign'),
        cell: ({ row }) => t(campaignLabelKey(row.original.campaign), row.original.campaign),
      },
      { id: 'mode', accessorKey: 'mode', header: t('DataImport.Mode', 'Mode') },
      { id: 'requestedBy', accessorKey: 'requestedBy', header: t('DataImport.RequestedBy', 'By') },
      {
        id: 'status',
        accessorKey: 'status',
        header: t('DataImport.Status', 'Status'),
        meta: { align: 'center' },
        cell: ({ row }) => (
          <span className={`badge ${STATUS_TONE[row.original.status]}`} title={row.original.error ?? undefined}>
            {row.original.status}
          </span>
        ),
      },
      {
        id: 'finishedAt',
        accessorKey: 'finishedAt',
        header: t('DataImport.Finished', 'Finished'),
        cell: ({ row }) => formatDateTime(row.original.finishedAt),
      },
      {
        id: 'view',
        header: '',
        enableSorting: false,
        meta: { align: 'center' },
        cell: ({ row }) => (
          <button
            type="button"
            className={`btn btn-sm ${row.original.id === selectedId ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => onSelect(row.original.id)}
            title={t('DataImport.ViewLog', 'View the log')}
          >
            <SpriteIcon name="eye" size={14} />
          </button>
        ),
      },
    ],
    [onSelect, selectedId, t],
  );

  const { gridProps } = useClientDataGrid<ImportJob>({
    rows: jobs,
    initialPageSize: 10,
    initialSorting: [{ id: 'createdAt', desc: true }],
  });

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-transparent">
        <h5 className="mb-0">{t('DataImport.History', 'Recent imports')}</h5>
      </div>
      <div className="card-body">
        <DataGrid<ImportJob>
          columns={columns}
          {...gridProps}
          isLoading={isLoading}
          getRowId={(row) => row.id}
          emptyMessage={t('DataImport.NoHistory', 'No imports have been run yet.')}
        />
      </div>
    </div>
  );
}
